import React, { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { FiMapPin, FiUsers, FiArrowRight, FiInfo, FiClock } from 'react-icons/fi'
import { fetchPublicHeritagePlaces } from '../utils/api'

const CountdownTimer = ({ timingDay, timingStart }) => {
  const [timeLeft, setTimeLeft] = useState('')

  useEffect(() => {
    if (!timingDay || !timingStart) {
      setTimeLeft('')
      return
    }

    const calculateTimeLeft = () => {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
      const targetDay = days.indexOf(timingDay)
      const today = new Date().getDay()
      
      let daysUntil = targetDay - today
      if (daysUntil < 0) daysUntil += 7
      if (daysUntil === 0) daysUntil = 0
      
      const [hours, minutes] = timingStart.split(':').map(Number)
      const now = new Date()
      const target = new Date(now)
      target.setDate(now.getDate() + daysUntil)
      target.setHours(hours, minutes, 0, 0)
      
      if (target <= now && daysUntil === 0) {
        target.setDate(target.getDate() + 7)
      }
      
      const diff = target - now
      const d = Math.floor(diff / (1000 * 60 * 60 * 24))
      const h = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const s = Math.floor((diff % (1000 * 60)) / 1000)
      
      setTimeLeft(`${d}d ${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`)
    }

    calculateTimeLeft()
    const timer = setInterval(calculateTimeLeft, 1000)
    return () => clearInterval(timer)
  }, [timingDay, timingStart])

  if (!timeLeft) return null
  return (
    <div className="flex items-center space-x-2 text-accent-maroon">
      <FiClock size={14} />
      <span className="text-sm font-mono font-bold">{timeLeft}</span>
    </div>
  )
}

const HeritageSection = () => {
  const [dbPlaces, setDbPlaces] = useState(null)
  const [hoveredIndex, setHoveredIndex] = useState(null)

  useEffect(() => {
    const getPlaces = async () => {
      try {
        const res = await fetchPublicHeritagePlaces()
        setDbPlaces(res.data?.data)
      } catch (err) {
        console.error('Failed to fetch public heritage places:', err)
      }
    }
    getPlaces()
  }, [])

  const defaultPlaces = [
    {
      name: 'Fateh Sagar Lake',
      description: 'The blue heart of Udaipur, famous for its solar observatory and evening vibes.',
      image_url: 'https://images.unsplash.com/photo-1590480370914-1e582855f756?auto=format&fit=crop&q=80',
      volunteers_active: '1245',
      timing_day: 'Saturday',
      timing_start: '06:00:00',
    },
    {
      name: 'Pichola Lake',
      description: 'An architectural marvel where history meets the shimmering water.',
      image_url: 'https://images.unsplash.com/photo-1595066922588-4660e1d9962a?auto=format&fit=crop&q=80',
      volunteers_active: '892',
      timing_day: 'Sunday',
      timing_start: '06:00:00',
    },
    {
      name: 'City Palace',
      description: 'The majestic centerpiece of Udaipur\'s royal history.',
      image_url: 'https://images.unsplash.com/photo-1626078302251-60a6316715f0?auto=format&fit=crop&q=80',
      volunteers_active: '734',
      timing_day: 'Wednesday',
      timing_start: '07:00:00',
    },
    {
      name: 'Gangaur Ghat',
      description: 'A spiritual gateway known for its cultural festivals and clean steps.',
      image_url: 'https://images.unsplash.com/photo-1595066922336-d4469796e94e?auto=format&fit=crop&q=80',
      volunteers_active: '567',
      timing_day: 'Friday',
      timing_start: '16:00:00',
    },
  ]

  const places = dbPlaces || defaultPlaces

  return (
    <section id="heritage" className="relative py-32 px-4 sm:px-6 lg:px-8 bg-gray-50 overflow-hidden">
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-accent-maroon/5 blur-[150px] rounded-full translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-accent-maroon/3 blur-[150px] rounded-full -translate-x-1/2 translate-y-1/2" />

      <div className="max-w-7xl mx-auto relative">
        <div className="mb-24 flex flex-col lg:flex-row lg:items-end justify-between gap-12">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-3xl">
            <h4 className="text-accent-maroon font-black tracking-[0.3em] uppercase text-xs mb-6">Discovery Mode</h4>
            <h2 className="text-6xl md:text-8xl font-black font-grotesk tracking-tighter leading-none text-gray-800">
              UDAIPUR <br />
              <span className="bg-gradient-to-r from-accent-maroon to-accent-maroonDark bg-clip-text text-transparent">LEGACY.</span>
            </h2>
          </motion.div>
          <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-gray-600 text-xl font-medium max-w-sm">
            Explore the timeless monuments and sacred water bodies we are fighting to preserve for eternity.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-32">
          {places.slice(0, 4).map((place, index) => {
            const isLarge = index === 0 || index === 3
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                className={`group relative overflow-hidden rounded-[2.5rem] bg-white/10 border border-gray-200 ${
                  isLarge ? 'md:col-span-7 h-[500px]' : 'md:col-span-5 h-[500px]'
                }`}
              >
                <div className="absolute inset-0 z-0">
                  <img src={place.image_url} alt={place.name} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110 opacity-40 group-hover:opacity-60" />
                  <div className="absolute inset-0 bg-gradient-to-t from-gray-50 via-transparent to-transparent opacity-80" />
                </div>

                <div className="absolute inset-0 z-10 p-10 flex flex-col justify-between">
                  <div className="flex justify-between items-start">
                    <div className="w-12 h-12 rounded-2xl bg-white/10 backdrop-blur-md border border-gray-200 flex items-center justify-center text-gray-800">
                      <FiMapPin size={20} />
                    </div>
                    <motion.div animate={{ opacity: hoveredIndex === index ? 1 : 0, y: hoveredIndex === index ? 0 : 10 }} className="bg-accent-maroon px-4 py-2 rounded-full text-white font-black text-[10px] uppercase tracking-widest">
                      LIVE DRIVE
                    </motion.div>
                  </div>

                  <div>
                    <h3 className="text-4xl font-black text-gray-800 mb-4 tracking-tighter leading-none">{place.name}</h3>
                    <AnimatePresence>
                      {hoveredIndex === index && (
                        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="space-y-6">
                          <p className="text-gray-600 font-medium text-sm leading-relaxed max-w-sm">{place.description}</p>
                          <div className="flex items-center space-x-6">
                            <div className="flex items-center space-x-2">
                              <FiUsers className="text-accent-maroon" />
                              <span className="text-gray-800 font-black text-sm">{place.volunteers_active}</span>
                            </div>
                            {place.timing_day && place.timing_start && (
                              <CountdownTimer timingDay={place.timing_day} timingStart={place.timing_start} />
                            )}
                            <button className="flex items-center space-x-2 text-accent-maroon font-black text-xs uppercase tracking-widest hover:underline">
                              <span>Join Now</span>
                              <FiArrowRight />
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>

        <motion.div initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="relative bg-white/10 border border-gray-200 rounded-[3rem] p-12 md:p-20 overflow-hidden backdrop-blur-2xl">
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(#800000 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
          
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-4 gap-16 items-center">
            <div className="lg:col-span-1">
              <div className="w-16 h-16 rounded-3xl bg-accent-maroon/10 border border-accent-maroon/20 flex items-center justify-center text-accent-maroon mb-8">
                <FiInfo size={32} />
              </div>
              <h3 className="text-3xl font-black text-gray-800 mb-4 tracking-tighter">OUR REAL-TIME <br />IMPACT.</h3>
              <p className="text-gray-600 font-medium text-sm">Quantifying the transformation of Udaipur, one mission at a time.</p>
            </div>

            <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-12">
              {[
                { label: 'VOLUNTEER HOURS', value: '45K+', sub: 'Active contribution' },
                { label: 'WASTE REMOVED', value: '125T', sub: 'Tons of plastic/waste' },
                { label: 'LIVES IMPACTED', value: '12K+', sub: 'Community growth' }
              ].map((stat, i) => (
                <div key={i} className="space-y-2">
                  <span className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em]">{stat.label}</span>
                  <div className="text-6xl md:text-7xl font-black text-gray-800 tracking-tighter bg-gradient-to-r from-accent-maroon to-accent-maroonDark bg-clip-text text-transparent italic">
                    {stat.value}
                  </div>
                  <p className="text-gray-600 text-xs font-bold">{stat.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}

export default HeritageSection